/**
 * Coupon Validation Utility
 *
 * This utility provides robust and dynamic validation logic for coupon codes
 * against form fields and order data. It handles various field types and
 * comparison methods dynamically based on the data structure.
 */

import {
  fieldCustomerSegment,
  fieldContractTerm,
  fieldBusinessConnectEdition,
  fieldDoesTheCustomerRequireWhiteGlove,
  fieldTypeOfInstallation,
  fieldOtherTelusServices,
  fieldProvinceOfTheOrder,
  fieldMultisiteOrder,
  fieldMinimumSeats,
  fieldStarterSeat,
  fieldOfficeSeats,
  fieldHowCustomerWantToPayForWhiteGlove
} from './constant';

import EN from '../../EN.json';
import FR from '../../FR.json';

/**
 * Map of French contentful display values → English equivalents.
 * Built once at module load from the already-imported locale JSON so that
 * coupon answers (always stored in English) can be compared against French
 * form field values without false mismatches.
 */
const FR_TO_EN_VALUE_MAP = (() => {
  const enValues = EN.contentfulValues || {};
  const frValues = FR.contentfulValues || {};
  const map = {};

  Object.keys(frValues).forEach(key => {
    const frVal = frValues[key];
    const enVal = enValues[key];

    if (typeof frVal === 'string' && typeof enVal === 'string') {
      const k = frVal.toLowerCase().trim();
      if (!map[k]) map[k] = enVal; // first-write-wins: avoids collisions (e.g. threeYears vs value3year both using "3 ans")
    } else if (
      frVal &&
      typeof frVal === 'object' &&
      enVal &&
      typeof enVal === 'object'
    ) {
      // Handle nested objects (e.g. editionMapping)
      Object.keys(frVal).forEach(subKey => {
        if (
          typeof frVal[subKey] === 'string' &&
          typeof enVal[subKey] === 'string'
        ) {
          const k = frVal[subKey].toLowerCase().trim();
          if (!map[k]) map[k] = enVal[subKey];
        }
      });
    }
  });

  return map;
})();

/**
 * Translate a French display value to its English equivalent using the
 * FR_TO_EN_VALUE_MAP. Supports both single strings and arrays (multi-select).
 * Falls back to the original value when no mapping is found.
 * @param {string|string[]} val
 * @returns {string|string[]}
 */
const normalizeLocaleToEn = val => {
  if (Array.isArray(val)) {
    return val.map(v => FR_TO_EN_VALUE_MAP[v.toLowerCase().trim()] || v);
  }
  if (!val) return val;
  return FR_TO_EN_VALUE_MAP[val.toLowerCase().trim()] || val;
};

/**
 * Normalize values for case-insensitive comparison
 * @param {any} value - The value to normalize
 * @returns {any} - Normalized value
 */
export const normalizeValue = value => {
  if (typeof value === 'string') {
    return value.toLowerCase().trim();
  }
  return value;
};

/**
 * Single field validation (handles comma-separated values)
 * @param {string} answerValue - The expected value from coupon answer
 * @param {any} userValue - The user's input value
 * @returns {boolean} - Whether the validation passes
 */
export const validateSingleField = (answerValue, userValue) => {
  const normalizedAnswer = normalizeValue(answerValue);
  const normalizedUser = normalizeValue(userValue);

  if (normalizedAnswer.includes(',')) {
    const allowedValues = normalizedAnswer.split(',').map(val => val.trim());
    return allowedValues.includes(normalizedUser);
  }

  return normalizedAnswer === normalizedUser;
};

/**
 * Multi-select field validation
 * @param {string} answerValue - The expected value from coupon answer
 * @param {any} userValue - The user's input value (can be array or single value)
 * @returns {boolean} - Whether the validation passes
 */
export const validateMultiField = (answerValue, userValue) => {
  const normalizedAnswer = normalizeValue(answerValue);

  if (Array.isArray(userValue)) {
    if (normalizedAnswer.includes(',')) {
      const allowedValues = normalizedAnswer.split(',').map(val => val.trim());
      return userValue.some(userVal =>
        allowedValues.includes(normalizeValue(userVal))
      );
    }
    return userValue.some(
      userVal => normalizedAnswer === normalizeValue(userVal)
    );
  }

  return validateSingleField(answerValue, userValue);
};

/**
 * Numeric field validation (for minimum/maximum values)
 * @param {string} answerValue - The expected value from coupon answer
 * @param {any} userValue - The user's input value
 * @returns {boolean} - Whether the validation passes
 */
export const validateNumericField = (answerValue, userValue) => {
  const userNum = parseFloat(userValue);

  if (Number.isNaN(userNum)) return false;

  // Extract numeric value from answer, handling comparison operators
  let answerNum;
  let operator = '';

  if (answerValue.includes('>=')) {
    operator = '>=';
    answerNum = parseFloat(answerValue.replace('>=', ''));
  } else if (answerValue.includes('<=')) {
    operator = '<=';
    answerNum = parseFloat(answerValue.replace('<=', ''));
  } else if (answerValue.includes('>')) {
    operator = '>';
    answerNum = parseFloat(answerValue.replace('>', ''));
  } else if (answerValue.includes('<')) {
    operator = '<';
    answerNum = parseFloat(answerValue.replace('<', ''));
  } else if (answerValue.includes('=')) {
    operator = '=';
    answerNum = parseFloat(answerValue.replace('=', ''));
  } else {
    // No operator, treat as exact match
    answerNum = parseFloat(answerValue);
  }

  if (Number.isNaN(answerNum)) return false;

  // Apply the comparison
  switch (operator) {
    case '>=':
      return userNum >= answerNum;
    case '<=':
      return userNum <= answerNum;
    case '>':
      return userNum > answerNum;
    case '<':
      return userNum < answerNum;
    case '=':
      return userNum === answerNum;
    default:
      // Default to exact match
      return userNum === answerNum;
  }
};

/**
 * Boolean field validation
 * @param {string} answerValue - The expected value from coupon answer
 * @param {any} userValue - The user's input value
 * @returns {boolean} - Whether the validation passes
 */
export const validateBooleanField = (answerValue, userValue) => {
  const normalizedAnswer = answerValue.toLowerCase().trim();
  const normalizedUser = userValue.toLowerCase().trim();

  // Handle different boolean representations
  const booleanValues = {
    true: ['true', 'yes', '1', 'on', 'enabled'],
    false: ['false', 'no', '0', 'off', 'disabled']
  };

  if (normalizedAnswer.includes(',')) {
    const allowedValues = normalizedAnswer.split(',').map(val => val.trim());
    return allowedValues.some(val => {
      const boolType = booleanValues[val] || [val];
      return boolType.includes(normalizedUser);
    });
  }

  const boolType = booleanValues[normalizedAnswer] || [normalizedAnswer];
  return boolType.includes(normalizedUser);
};

/**
 * Date field validation
 * @param {string} answerValue - The expected value from coupon answer
 * @param {any} userValue - The user's input value
 * @returns {boolean} - Whether the validation passes
 */
export const validateDateField = (answerValue, userValue) => {
  try {
    const answerDate = new Date(answerValue);
    const userDate = new Date(userValue);

    if (Number.isNaN(answerDate.getTime()) || Number.isNaN(userDate.getTime()))
      return false;

    // Check if answer contains date range
    if (answerValue.includes('-')) {
      const [startDate, endDate] = answerValue
        .split('-')
        .map(d => new Date(d.trim()));
      return userDate >= startDate && userDate <= endDate;
    }

    return answerDate.getTime() === userDate.getTime();
  } catch (error) {
    return false;
  }
};

/**
 * Auto-detect field type based on value structure
 * @param {string} answerValue - The expected value from coupon answer
 * @param {any} userValue - The user's input value
 * @returns {boolean} - Whether the validation passes
 */
export const autoDetectFieldType = (answerValue, userValue) => {
  // Check if it's a numeric field
  if (
    !Number.isNaN(parseFloat(answerValue)) &&
    !Number.isNaN(parseFloat(userValue))
  ) {
    return validateNumericField(answerValue, userValue);
  }

  // Check if it's a boolean field
  const booleanKeywords = ['true', 'false', 'yes', 'no', '1', '0', 'on', 'off'];
  if (
    booleanKeywords.includes(normalizeValue(answerValue)) ||
    booleanKeywords.includes(normalizeValue(userValue))
  ) {
    return validateBooleanField(answerValue, userValue);
  }

  // Check if it's a date field
  if (
    !Number.isNaN(Date.parse(answerValue)) ||
    !Number.isNaN(Date.parse(userValue))
  ) {
    return validateDateField(answerValue, userValue);
  }

  // Default to single field validation
  return validateSingleField(answerValue, userValue);
};

/**
 * Dynamic comparison logic that handles different field types and comparison methods
 * @param {string} fieldName - The name of the field being validated
 * @param {string} answerValue - The expected value from coupon answer
 * @param {any} userValue - The user's input value
 * @param {string} fieldType - The type of field ('single', 'multi', 'numeric', 'boolean', 'date', or 'auto')
 * @returns {boolean} - Whether the validation passes
 */
export const validateFieldMatch = (
  fieldName,
  answerValue,
  userValue,
  fieldType = 'auto'
) => {
  if (!answerValue || !userValue) return true; // If no answer value, consider it valid

  // Handle different field types
  switch (fieldType) {
    case 'single':
      return validateSingleField(answerValue, userValue);

    case 'multi':
      return validateMultiField(answerValue, userValue);

    case 'numeric':
      return validateNumericField(answerValue, userValue);

    case 'boolean':
      return validateBooleanField(answerValue, userValue);

    case 'date':
      return validateDateField(answerValue, userValue);

    case 'auto':
    default:
      // Auto-detect field type based on value structure
      return autoDetectFieldType(answerValue, userValue);
  }
};

const toIdString = value =>
  value === undefined || value === null || value === '' ? '' : String(value);

/**
 * Resolve the channel / dealership IDs the order belongs to. Orders carry the
 * creator's profile (channels, Dealership) in meta, plus the `channelId` column
 * once the quote step is submitted — so admins/SRT reviewing an order are
 * evaluated against the order owner, not themselves.
 * @param {Object} orderData
 * @returns {{ channelId: string, dealershipId: string }}
 */
export const getOrderChannelAndDealership = (orderData = {}) => {
  const data = orderData || {};
  const channels = Array.isArray(data.channels) ? data.channels : [];
  const channelId = data.channelId || (channels[0] && channels[0].id);
  const dealershipId =
    (data.Dealership && data.Dealership.id) || data.dealership;
  return {
    channelId: toIdString(channelId),
    dealershipId: toIdString(dealershipId)
  };
};

/**
 * Dynamic field mapping configuration
 * This configuration maps coupon answer keys to their corresponding values in formFields and orderData
 * It's designed to be extensible and handle any field keys dynamically
 */
export const createFieldMapping = (formFields, orderData, isFrench = false) => {
  // When the form is in French, translate display values back to English so
  // they can be compared against coupon answers (which are stored in English).
  const localeNorm = val => (isFrench ? normalizeLocaleToEn(val) : val);
  const { channelId, dealershipId } = getOrderChannelAndDealership(orderData);

  // Base field mapping that covers all possible coupon answer keys
  const baseFieldMapping = {
    // Coupon answers store comma-separated channel / dealership IDs
    channel: {
      userValue: channelId,
      displayName: isFrench ? 'Réseau' : 'Channel',
      type: 'single'
    },
    dealership: {
      userValue: dealershipId,
      displayName: isFrench ? 'Concessionnaire' : 'Dealership',
      type: 'single'
    },
    // Customer and Order Information
    customerSegment: {
      userValue: orderData[fieldCustomerSegment],
      displayName: isFrench ? 'Segment Client' : 'Customer Segment',
      type: 'single'
    },
    provinceOfTheOrder: {
      userValue: orderData[fieldProvinceOfTheOrder],
      displayName: isFrench ? 'Province' : 'Province',
      type: 'single'
    },
    multisiteOrder: {
      // Normalize to canonical Yes/No — handles 'oui'/'non'/empty/undefined so both
      // loops work correctly for EN, FR, and orders where the field is not shown.
      userValue: ['yes', 'oui'].includes(
        (orderData[fieldMultisiteOrder] || '').toLowerCase().trim()
      )
        ? 'Yes'
        : 'No',
      displayName: isFrench ? 'Commande Multi-Sites' : 'Multisite Order',
      type: 'boolean'
    },
    minimumSeats: {
      userValue: orderData[fieldMinimumSeats],
      displayName: isFrench ? 'Sièges Minimum' : 'Minimum Seats',
      type: 'numeric'
    },

    // Form Fields - Contract and Service Information
    contractTerm: {
      userValue: localeNorm(
        formFields[fieldContractTerm] && formFields[fieldContractTerm].value
      ),
      displayName: isFrench ? 'Durée du Contrat' : 'Contract Term',
      type: 'single'
    },
    businessConnectEdition: {
      userValue: localeNorm(
        formFields[fieldBusinessConnectEdition] &&
          formFields[fieldBusinessConnectEdition].value
      ),
      displayName: isFrench
        ? 'Édition Business Connect'
        : 'Business Connect Edition',
      type: 'single'
    },
    doesTheCustomerRequireWhiteGlove: {
      userValue: localeNorm(
        formFields[fieldDoesTheCustomerRequireWhiteGlove] &&
          formFields[fieldDoesTheCustomerRequireWhiteGlove].value
      ),
      displayName: isFrench ? 'Service White Glove' : 'White Glove Service',
      type: 'boolean'
    },
    typeOfInstallation: {
      userValue: localeNorm(
        formFields[fieldTypeOfInstallation] &&
          formFields[fieldTypeOfInstallation].value
      ),
      displayName: isFrench ? "Type d'Installation" : 'Installation Type',
      type: 'single'
    },
    otherTelusServices: {
      userValue: localeNorm(
        formFields[fieldOtherTelusServices] &&
          formFields[fieldOtherTelusServices].value
      ),
      displayName: isFrench ? 'Autres Services TELUS' : 'Other Telus Services',
      type: 'multi'
    },
    howCustomerWantToPayForWhiteGlove: {
      userValue: localeNorm(
        (formFields[fieldHowCustomerWantToPayForWhiteGlove] &&
          formFields[fieldHowCustomerWantToPayForWhiteGlove].value) ||
          orderData[fieldHowCustomerWantToPayForWhiteGlove]
      ),
      displayName: isFrench
        ? 'Mode de Paiement White Glove'
        : 'White Glove Payment Type',
      type: 'single'
    }
  };

  // Dynamic field mapping that handles any field keys not explicitly defined
  const dynamicFieldMapping = new Proxy(baseFieldMapping, {
    get(target, prop) {
      // If the property exists in the base mapping, return it
      if (prop in target) {
        return target[prop];
      }

      // For any other field key, try to find it dynamically
      if (typeof prop === 'string') {
        // Check if it exists in formFields
        const formField = formFields[prop];
        const formFieldValue = formField && formField.value;
        if (formFieldValue !== undefined) {
          return {
            userValue: formFieldValue,
            displayName: prop.charAt(0).toUpperCase() + prop.slice(1),
            type: 'auto'
          };
        }

        // Check if it exists in orderData
        const orderDataValue = orderData[prop];
        if (orderDataValue !== undefined) {
          return {
            userValue: orderDataValue,
            displayName: prop.charAt(0).toUpperCase() + prop.slice(1),
            type: 'auto'
          };
        }
      }

      // Return undefined if field is not found
      return undefined;
    }
  });

  return dynamicFieldMapping;
};

/**
 * Validate coupon against user selections using dynamic field mapping
 * @param {Object} coupon - The coupon object with answers
 * @param {Object} formFields - The form fields data
 * @param {Object} orderData - The order data
 * @param {boolean} isFrench - Whether to use French locale
 * @param {Object} options - Additional options (currentSite, isMultisiteOrder)
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateCouponAgainstUserSelections = (
  coupon,
  formFields,
  orderData,
  isFrench = false,
  options = {}
) => {
  const { currentSite = 1, isMultisiteOrder = false } = options;

  // Skip validation for multi-site orders on sites other than site 1
  // Coupons are only applied on site 1, so validation should only happen there
  if (isMultisiteOrder && currentSite > 1) {
    return {
      isValid: true,
      errors: [],
      message: ''
    };
  }

  const {
    contentfulValues: { valueTelusProfessionalInstallation } = {}
  } = isFrench ? FR : EN;
  const answers = coupon.answers || {};
  const validationErrors = [];
  const fieldMapping = createFieldMapping(formFields, orderData, isFrench);
  const isMultisite = ['yes', 'oui'].includes(
    normalizeValue(orderData[fieldMultisiteOrder] || '')
  );
  const minimumOrderSeats = normalizeValue(
    (formFields[fieldMinimumSeats] && formFields[fieldMinimumSeats].value) ||
      orderData[fieldMinimumSeats] ||
      ''
  );
  const nonMultisiteOrderSeats =
    Number(
      (formFields[fieldStarterSeat] && formFields[fieldStarterSeat].value) ||
        orderData[fieldStarterSeat] ||
        0
    ) +
    Number(
      (formFields[fieldOfficeSeats] && formFields[fieldOfficeSeats].value) ||
        orderData[fieldOfficeSeats] ||
        0
    );
  const isInstallationProfessional =
    normalizeValue(valueTelusProfessionalInstallation) ===
    normalizeValue(
      (formFields[fieldDoesTheCustomerRequireWhiteGlove] &&
        formFields[fieldDoesTheCustomerRequireWhiteGlove].value) ||
        ''
    );
  const couponData = coupon.coupon || coupon;
  const couponType = (couponData.couponType || '').toLowerCase();
  const isInstallationCoupon = couponType.includes('installation');

  // Check if coupon requires any fields that are missing in the webform
  Object.keys(answers).forEach(answerKey => {
    const fieldInfo = fieldMapping[answerKey];
    if (fieldInfo && answers[answerKey]) {
      // Special case: Skip province validation if coupon answer is "all provinces"
      const normalizedAnswer = normalizeValue(answers[answerKey]);
      if (
        answerKey === 'provinceOfTheOrder' &&
        (normalizedAnswer.includes('all provinces') ||
          normalizedAnswer.includes('toutes les provinces'))
      ) {
        return; // Skip validation for this field
      }

      if (answerKey === 'minimumSeats') {
        const parsedMinimumSeats = parseFloat(minimumOrderSeats);
        // For multisite, use the aggregate minimumSeats when available; fall back to
        // current site's seat count when minimumSeats hasn't been populated yet (e.g. site 1).
        const orderSeatCount =
          isMultisite && !Number.isNaN(parsedMinimumSeats)
            ? parsedMinimumSeats
            : nonMultisiteOrderSeats;
        if (
          Number.isNaN(orderSeatCount) ||
          orderSeatCount < parseFloat(normalizeValue(answers[answerKey]))
        ) {
          validationErrors.push(`${fieldInfo.displayName} does not match`);
        }
        return;
      }
      if (
        answerKey === 'howCustomerWantToPayForWhiteGlove' &&
        !isInstallationProfessional
      ) {
        return;
      }

      // Dealership only narrows orders placed by a dealership; orders without one are gated by channel alone
      if (answerKey === 'dealership' && !fieldInfo.userValue) {
        return;
      }

      if (!fieldInfo.userValue || fieldInfo.userValue === '') {
        validationErrors.push(
          `Please select a value for ${fieldInfo.displayName} before applying this coupon`
        );
      }
    }
  });

  // If there are missing required fields, return early
  if (validationErrors.length > 0) {
    // eslint-disable-next-line no-console
    console.error('Field Validation Error', validationErrors);
    return {
      isValid: false,
      errors: validationErrors,
      message: isFrench
        ? 'Le code promotionnel ne répond pas aux critères d’admissibilité'
        : 'Coupon code does not meet eligibility criteria'
    };
  }

  // Validate each field dynamically
  // Special case: For installation coupons with Professional Installation, validate duration matches payment type
  // This needs to be checked from field mapping directly (not in answers)
  if (isInstallationProfessional && isInstallationCoupon) {
    const paymentFieldInfo = fieldMapping.howCustomerWantToPayForWhiteGlove;
    if (paymentFieldInfo && paymentFieldInfo.userValue) {
      const duration = (couponData.duration || '').toLowerCase();
      const isCouponMonthly = duration.includes('monthly');
      const paymentType = normalizeValue(paymentFieldInfo.userValue || '');
      const isPaymentMonthly =
        paymentType.includes('easy payment') ||
        paymentType.includes('paiements faciles');

      // Monthly coupon requires Easy Payment, one-time coupon requires Upfront
      if (
        (isCouponMonthly && !isPaymentMonthly) ||
        (!isCouponMonthly && isPaymentMonthly)
      ) {
        validationErrors.push(`${paymentFieldInfo.displayName} does not match`);
      }
    }
  }

  Object.keys(answers).forEach(answerKey => {
    const fieldInfo = fieldMapping[answerKey];
    const answerValue = answers[answerKey];

    if (fieldInfo && answerValue) {
      // Special case: Skip province validation if coupon answer is "all provinces"
      const normalizedAnswer = normalizeValue(answerValue);
      if (
        answerKey === 'provinceOfTheOrder' &&
        (normalizedAnswer.includes('all provinces') ||
          normalizedAnswer.includes('toutes les provinces'))
      ) {
        return; // Skip validation for this field
      }

      if (answerKey === 'minimumSeats') {
        const parsedMinimumSeats = parseFloat(minimumOrderSeats);
        const orderSeatCount =
          isMultisite && !Number.isNaN(parsedMinimumSeats)
            ? parsedMinimumSeats
            : nonMultisiteOrderSeats;
        if (
          Number.isNaN(orderSeatCount) ||
          orderSeatCount < parseFloat(normalizeValue(answerValue))
        ) {
          validationErrors.push(`${fieldInfo.displayName} does not match`);
        }
        return;
      }

      if (
        !validateFieldMatch(
          answerKey,
          answerValue,
          fieldInfo.userValue,
          fieldInfo.type
        )
      ) {
        validationErrors.push(`${fieldInfo.displayName} does not match`);
      }
    }
  });
  // eslint-disable-next-line no-console
  console.error('Field Validation Error', validationErrors);

  const validationMessage = isFrench
    ? "Ce code de coupon ne peut pas être validé par rapport aux sélections de l'utilisateur"
    : "This coupon code can't be validated against user selections";

  return {
    isValid: validationErrors.length === 0,
    errors: validationErrors,
    message: validationErrors.length > 0 ? validationMessage : ''
  };
};

/**
 * Find coupon by code from a list of coupons
 * @param {Array} coupons - Array of coupon objects
 * @param {string} couponCode - The coupon code to find
 * @returns {Object|null} - The found coupon or null
 */
export const findCouponByCode = (coupons, couponCode) => {
  if (!Array.isArray(coupons) || !couponCode) {
    return null;
  }

  const normalizedCouponCode = couponCode.trim().toLowerCase();

  return coupons.find(c => {
    const couponCodeValue = (c.coupon && c.coupon.couponCode) || c.couponCode;
    return (
      couponCodeValue &&
      couponCodeValue.trim().toLowerCase() === normalizedCouponCode
    );
  });
};

/**
 * Check if coupon has expired
 * @param {Object} coupon - The coupon object
 * @returns {boolean} - Whether the coupon has expired
 */
export const isCouponExpired = coupon => {
  const now = new Date();
  const expireDate =
    (coupon.coupon && coupon.coupon.expireDate) || coupon.expireDate;

  return expireDate && new Date(expireDate) < now;
};

/**
 * Main coupon validation function
 * @param {string} couponCode - The coupon code to validate
 * @param {Array} coupons - Array of available coupons
 * @param {Object} formFields - The form fields data
 * @param {Object} orderData - The order data
 * @param {boolean} isFrench - Whether to use French locale
 * @param {Object} options - Additional options (currentSite, isMultisiteOrder)
 * @returns {Object} - Validation result
 */
export const validateCoupon = async (
  couponCode,
  coupons,
  formFields,
  orderData,
  isFrench = false,
  options = {}
) => {
  // Normalize coupon code by trimming whitespace
  const normalizedCouponCode = couponCode.trim();

  if (!normalizedCouponCode) {
    return {
      isValid: false,
      error: 'Please enter a coupon code',
      coupon: null
    };
  }

  // Find the coupon by code (using normalized code)
  const coupon = findCouponByCode(coupons, normalizedCouponCode);

  if (!coupon) {
    return {
      isValid: false,
      error: 'Invalid coupon code',
      coupon: null
    };
  }

  // Check if coupon has expired
  if (isCouponExpired(coupon)) {
    return {
      isValid: false,
      error: 'Coupon has expired',
      coupon: null
    };
  }

  // Validate against user selections
  const validationResult = validateCouponAgainstUserSelections(
    coupon,
    formFields,
    orderData,
    isFrench,
    options
  );

  if (!validationResult.isValid) {
    return {
      isValid: false,
      error: validationResult.message,
      coupon: null
    };
  }

  // Coupon is valid
  return {
    isValid: true,
    error: '',
    coupon
  };
};
